import { useState } from "react";
import { CalendarDays, Clock, User, Phone, Mail, Check, Trash2, MessageCircle, BellRing, CalendarX, Euro, UserX, StickyNote, AlertTriangle, MapPin, Pencil, Star } from "lucide-react";
import { fmtDate } from "@/components/admin/useAppointments";
import EditAppointmentDialog from "@/components/admin/EditAppointmentDialog";

const STATUS_STYLES = {
  pendiente: "bg-amber-100 text-amber-800",
  contactada: "bg-blue-100 text-blue-800",
  confirmada: "bg-emerald-100 text-emerald-800",
  completada: "bg-gold text-white",
  no_show: "bg-red-600 text-white",
  cancelada: "bg-red-100 text-red-700",
};
const STATUS_LABEL = { completada: "ha venido", no_show: "no ha venido" };
const parsePrice = (t) => {
  const m = (t || "").match(/\d+(?:[.,]\d+)?/);
  return m ? m[0].replace(",", ".") : "";
};

const waLink = (a, text) => `https://wa.me/${a.phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}`;
const contactText = (a) => `Hola ${a.name}, soy Isi de ISI'S Nails. Te escribo para confirmar tu cita de ${a.service} el ${fmtDate(a.date)} a las ${a.time}.`;
const reminderText = (a) => {
  const isTomorrow = a.date === new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const isToday = a.date === new Date().toISOString().slice(0, 10);
  const when = isToday ? "hoy" : isTomorrow ? "mañana" : `el ${fmtDate(a.date)}`;
  const where = a.location === "domicilio" ? ` en tu domicilio (${a.address})` : " en el estudio";
  return `Hola ${a.name} 💅 Soy Isi de ISI'S Nails. Te recuerdo tu cita de ${a.service} ${when} a las ${a.time}${where}. Si no puedes venir, avísame con tiempo, por favor. ¡Nos vemos!`;
};

const reviewText = (a, link) =>
  `Hola ${a.name} 💅 ¡Gracias por tu visita! Si te han gustado tus uñas, ¿me dejas una reseña en Google? Me ayuda muchísimo y te lleva solo 30 segundos: ${link}`;

export default function AppointmentCard({ a, services, onUpdate, onDelete, onEdited, onResend, headers, reviewLink = "", compact = false }) {
  const [completing, setCompleting] = useState(false);
  const [amount, setAmount] = useState("");
  const [editing, setEditing] = useState(false);

  const startComplete = () => {
    const svc = services.find((s) => s.name === a.service);
    setAmount(a.price != null ? String(a.price) : a.estimated != null ? String(a.estimated) : parsePrice(svc?.price));
    setCompleting(true);
  };
  const saveComplete = async (e) => {
    e.preventDefault();
    const res = await onUpdate(a.id, { status: "completada", price: amount === "" ? null : Number(amount) });
    if (res) setCompleting(false);
  };

  const done = a.status === "completada" || a.status === "no_show" || a.status === "cancelada";
  const btn = "inline-flex items-center gap-1 rounded-full text-xs font-medium px-3 py-1.5 transition-colors";

  return (
    <article data-testid={`appointment-card-${a.id}`} className="rounded-2xl bg-creamcard border border-sand p-5 sm:p-6 flex flex-col sm:flex-row sm:items-start gap-5">
      <div className="sm:w-44 shrink-0">
        {!compact && (
          <p className="flex items-center gap-2 text-sm font-medium text-obsidian capitalize">
            <CalendarDays className="w-4 h-4 text-gold" /> {fmtDate(a.date)}
          </p>
        )}
        <p className={`flex items-center gap-2 ${compact ? "font-display text-2xl text-obsidian" : "text-sm text-taupe mt-1.5"}`}>
          <Clock className="w-4 h-4 text-gold" /> {a.time} h
        </p>
      </div>

      <div className="flex-1 min-w-0">
        <p className="flex items-center gap-2 text-sm font-medium text-obsidian flex-wrap">
          <User className="w-4 h-4 text-gold" /> {a.name}
          {a.no_shows > 0 && (
            <span data-testid={`appointment-noshows-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-[11px] px-2 py-0.5">
              <AlertTriangle className="w-3 h-3" /> {a.no_shows} falta{a.no_shows > 1 ? "s" : ""}
            </span>
          )}
        </p>
        <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-sm text-taupe hover:text-gold mt-1.5 transition-colors">
          <Phone className="w-4 h-4 text-gold" /> {a.phone}
        </a>
        {a.email && (
          <a href={`mailto:${a.email}`} data-testid={`appointment-email-${a.id}`} className="flex items-center gap-2 text-sm text-taupe hover:text-gold mt-1.5 transition-colors truncate">
            <Mail className="w-4 h-4 text-gold shrink-0" /> {a.email}
          </a>
        )}
        <p className="text-xs text-gold font-medium mt-1.5" data-testid={`appointment-services-${a.id}`}>
          {(a.items || []).map((it) => `${it.name}${it.qty > 1 ? ` ×${it.qty}` : ""}`).join(" + ") || a.service}
          {a.estimated != null && a.status !== "completada" && <span className="text-taupe font-normal"> · aprox. {a.estimated} €</span>}
        </p>
        {a.location === "domicilio" ? (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`appointment-location-${a.id}`}
            className="mt-1.5 flex items-start gap-1.5 text-xs text-cocoa hover:text-gold transition-colors"
          >
            <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gold" /> <span><b>A domicilio:</b> {a.address}</span>
          </a>
        ) : (
          <p data-testid={`appointment-location-${a.id}`} className="mt-1.5 flex items-center gap-1.5 text-xs text-taupe">
            <MapPin className="w-3.5 h-3.5 text-gold" /> En el estudio
          </p>
        )}
        {a.client_notes && (
          <p data-testid={`appointment-notes-${a.id}`} className="mt-2 flex items-start gap-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 whitespace-pre-line">
            <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {a.client_notes}
          </p>
        )}
        {a.reminder_sent && <Flag testid={`appointment-reminder-${a.id}`} cls="text-emerald-700" Icon={BellRing} text="Recordatorio enviado" />}
        {a.request_email === "enviado" && <Flag testid={`appointment-request-email-${a.id}`} cls="text-emerald-700" Icon={Mail} text="Email de «solicitud recibida» enviado" />}
        {a.request_email === "error" && <Flag testid={`appointment-request-email-${a.id}`} cls="text-red-700" Icon={Mail} text="No se pudo enviar el email de solicitud" />}
        {a.confirmation_email === "enviado" && <Flag testid={`appointment-confirm-email-${a.id}`} cls="text-emerald-700" Icon={Mail} text="Email de confirmación enviado" />}
        {a.confirmation_email === "error" && <Flag testid={`appointment-confirm-email-${a.id}`} cls="text-red-700" Icon={Mail} text="No se pudo enviar el email de confirmación" />}
        {a.confirmation_email === "sin_correo" && <Flag testid={`appointment-confirm-email-${a.id}`} cls="text-amber-700" Icon={Mail} text="Esta cita no tiene correo, avísale por WhatsApp" />}
        {a.cancelled_by === "clienta" && <Flag testid={`appointment-cancelled-by-${a.id}`} cls="text-red-700 font-medium" Icon={CalendarX} text="Cancelada por la clienta desde su email" />}
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:max-w-[320px] sm:justify-end">
        <span data-testid={`appointment-status-${a.id}`} className={`rounded-full text-xs font-medium px-3 py-1 capitalize ${STATUS_STYLES[a.status] || ""}`}>
          {STATUS_LABEL[a.status] || a.status}{a.status === "completada" && a.price != null ? ` · ${a.price} €` : ""}
        </span>
        {a.created_by === "isi" && <span data-testid={`appointment-by-isi-${a.id}`} className="rounded-full bg-sand/60 text-taupe text-[11px] px-2 py-0.5">creada por ti</span>}

        {!done && (
          <a href={waLink(a, reminderText(a))} target="_blank" rel="noopener noreferrer" data-testid={`appointment-remind-${a.id}`} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}>
            <BellRing className="w-3.5 h-3.5" /> Recordar por WhatsApp
          </a>
        )}
        <a href={waLink(a, contactText(a))} target="_blank" rel="noopener noreferrer" data-testid={`appointment-whatsapp-${a.id}`} className={`${btn} border border-gold text-gold hover:bg-gold hover:text-white`}>
          <MessageCircle className="w-3.5 h-3.5" /> Contactar
        </a>
        {a.status === "pendiente" && (
          <button onClick={() => onUpdate(a.id, { status: "contactada" })} data-testid={`appointment-contacted-btn-${a.id}`} className={`${btn} border border-sand text-taupe hover:text-obsidian`}>
            Contactada
          </button>
        )}
        {a.status !== "confirmada" && !done && (
          <button onClick={() => onUpdate(a.id, { status: "confirmada" })} data-testid={`appointment-confirm-btn-${a.id}`} className={`${btn} bg-emerald-100 hover:bg-emerald-200 text-emerald-800`}>
            <Check className="w-3.5 h-3.5" /> Confirmar
          </button>
        )}
        {a.status !== "cancelada" && !completing && (
          <button onClick={startComplete} data-testid={`appointment-complete-btn-${a.id}`} className={`${btn} bg-gold hover:bg-gold-dark text-white`}>
            <Euro className="w-3.5 h-3.5" /> {a.status === "completada" ? "Editar importe" : "Ha venido"}
          </button>
        )}
        {completing && (
          <form onSubmit={saveComplete} data-testid={`appointment-complete-form-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-gold bg-cream pl-3 pr-1 py-1">
            <input type="number" min="0" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus data-testid={`appointment-amount-input-${a.id}`} className="w-16 bg-transparent text-sm text-obsidian focus:outline-none" />
            <span className="text-xs text-taupe">€</span>
            <button type="submit" data-testid={`appointment-complete-save-${a.id}`} className="rounded-full bg-gold text-white p-1.5"><Check className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={() => setCompleting(false)} data-testid={`appointment-complete-cancel-${a.id}`} className="text-taupe text-xs px-1.5">✕</button>
          </form>
        )}
        {a.status !== "cancelada" && a.status !== "no_show" && a.status !== "completada" && (
          <button onClick={() => onUpdate(a.id, { status: "no_show" })} data-testid={`appointment-noshow-btn-${a.id}`} className={`${btn} border border-red-200 text-red-700 hover:bg-red-50`}>
            <UserX className="w-3.5 h-3.5" /> No ha venido
          </button>
        )}
        {a.email && !done && onResend && (
          <button onClick={() => onResend(a.id)} data-testid={`appointment-resend-email-${a.id}`} className={`${btn} border border-sand text-cocoa hover:border-gold hover:text-gold`}>
            <Mail className="w-3.5 h-3.5" /> Reenviar correo
          </button>
        )}
        {a.status === "completada" && reviewLink && (
          <a href={waLink(a, reviewText(a, reviewLink))} target="_blank" rel="noopener noreferrer" data-testid={`appointment-review-${a.id}`} className={`${btn} bg-rosesoft border border-gold text-gold hover:bg-gold hover:text-white`}>
            <Star className="w-3.5 h-3.5" /> Pedir reseña
          </a>
        )}
        {onEdited && (
          <button onClick={() => setEditing(true)} data-testid={`appointment-edit-btn-${a.id}`} className={`${btn} border border-sand text-cocoa hover:border-gold hover:text-gold`}>
            <Pencil className="w-3.5 h-3.5" /> Editar
          </button>
        )}
        {onEdited && (
          <EditAppointmentDialog
            appointment={a}
            open={editing}
            onClose={() => setEditing(false)}
            onSaved={onEdited}
            headers={headers}
          />
        )}
        <button onClick={() => onDelete(a.id)} data-testid={`appointment-delete-btn-${a.id}`} className="rounded-full border border-sand text-taupe hover:text-red-600 hover:border-red-300 p-1.5 transition-colors" aria-label="Eliminar cita">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </article>
  );
}

function Flag({ testid, cls, Icon, text }) {
  return (
    <p data-testid={testid} className={`flex items-center gap-1 text-xs mt-1 ${cls}`}>
      <Icon className="w-3 h-3" /> {text}
    </p>
  );
}
